import { useQuery } from "@tanstack/react-query";
import { queryClient } from "../services/queryClient";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../services/supabase";
import { getErrorMessage } from "@utils/helper";

/**
 * Pulls the current student's profile fields from the real schema.
 *
 * The StudentProfile page used to read everything from `user.details.*`
 * (e.g. `user.details.id_number`, `user.details.student_grade_level
 * .curriculum.curriculum_name`). But the `users` table has no `details`
 * column — student-specific fields live on the `students` table, and the
 * grade-level / curriculum / school-year linkage lives in
 * `student_grade_levels`. As a result, every profile field rendered
 * blank or "-" in production. Surfaced 2026-05-16 in bug 3d623af7.
 *
 * This hook does the proper join in one place so the UI can stay simple.
 */
export interface StudentProfileDetails {
  id_number: string | null;
  dob: string | null;
  user_name: string | null;
  contact_number: string | null;
  grade_level: string | null;
  curriculum_name: string | null;
  school_year_name: string | null;
  class_name: string | null;
  // "Program" is NOT a derived/joined value — it's a curriculum custom field.
  // Profile screens read it (and any other tenant-defined attribute) via
  // useStudentCurriculumCustomFields(), not from here.
  // No per-student "expiring on" column exists in the schema yet; surfaced as
  // null so the profile renders the labelled row with a "—" placeholder.
  expiring_on: string | null;
}

export function useStudentProfileDetails(): {
  details: StudentProfileDetails | null;
  loading: boolean;
  error: string | null;
} {
  const { profile } = useAuth();
  const studentId = profile?.student_id ?? null;
  const query = useQuery(
    {
      queryKey: ["student", "profileDetails", studentId],
      enabled: studentId != null,
      staleTime: 0,
      retry: false,
      queryFn: async (): Promise<StudentProfileDetails> => {
        const { data: student, error: sErr } = await supabase
          .from("students")
          .select("id_number, dob, user_name, contact_number")
          .eq("id", studentId as string)
          .maybeSingle();
        if (sErr) throw sErr;

        const { data: sgl, error: gErr } = await supabase
          .from("student_grade_levels")
          .select(
            "active, updated_at, grade_levels(grade_level), school_years(name, curriculums(curriculum_name))",
          )
          .eq("student_id", studentId as string)
          .order("active", { ascending: false })
          .order("updated_at", { ascending: false })
          .limit(1);
        if (gErr) throw gErr;
        const top = (sgl ?? [])[0];
        const first = <V>(v: V | V[] | null | undefined): V | undefined =>
          Array.isArray(v) ? v[0] : (v ?? undefined);
        const gradeLevel = first(top?.grade_levels);
        const schoolYear = first(top?.school_years);
        const curriculum = first(schoolYear?.curriculums);

        const { data: classRows } = await supabase
          .from("class_students")
          .select("classes(class_name)")
          .eq("student_id", studentId as string);
        const firstClass = first((classRows ?? [])[0]?.classes);
        const className = firstClass?.class_name ?? null;

        return {
          id_number: student?.id_number ?? null,
          dob: student?.dob ?? null,
          user_name: student?.user_name ?? null,
          contact_number: student?.contact_number ?? null,
          grade_level: gradeLevel?.grade_level ?? null,
          curriculum_name: curriculum?.curriculum_name ?? null,
          school_year_name: schoolYear?.name ?? null,
          class_name: className,
          expiring_on: null,
        };
      },
    },
    queryClient,
  );

  if (!studentId) return { details: null, loading: true, error: null };

  return {
    details: query.isError ? null : (query.data ?? null),
    loading: query.isPending,
    error: query.isError ? getErrorMessage(query.error, 'Failed to load profile details') : null,
  };
}
